import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { FeedbackCard } from 'modules/feedback';
import * as ResultModule from 'modules/result';
import { SCOPE_SKILL_KEY } from '../../constants';
import { finishedRows, hasPendingRow } from './finishedRows';

interface Props {
  attemptId: string;
  testTitle: string;
  candidateName?: string;
  onSeeResult: () => void;
  onBack: () => void;
}

const POLL_MS = 10_000;
const MAX_POLLS = 12;

/** After the last IELTS section is handed in: what has been marked, what is still being graded, and a word on the test. */
export function ExamFinishedScreen({ attemptId, testTitle, candidateName, onSeeResult, onBack }: Props) {
  const { t } = useTranslation();
  const { item, refetch } = ResultModule.Hooks.useSingle({ id: attemptId });
  const rows = useMemo(() => finishedRows(item?.scaledReport), [item?.scaledReport]);
  const waiting = rows.length === 0 || hasPendingRow(rows);

  // Look again while something is still being graded, for a couple of minutes at most.
  useEffect(() => {
    if (!waiting) return undefined;
    let polls = 0;
    const id = setInterval(() => {
      polls += 1;
      if (polls > MAX_POLLS) clearInterval(id);
      else void refetch();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [waiting, refetch]);

  return (
    <div className="flex min-h-[100dvh] flex-col bg-[#F4F6FB] text-[#10182B]">
      <header className="flex min-h-14 shrink-0 items-center justify-between gap-3 border-b border-[#E6E9F2] bg-white px-4 py-2 sm:h-16 sm:px-8 sm:py-0">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[9px] bg-[#D1FE17] font-display text-base font-extrabold" aria-hidden>
            E
          </span>
          <b className="line-clamp-2 text-sm leading-tight">{testTitle}</b>
        </div>
        {candidateName && <span className="hidden truncate text-[13px] text-[#646D84] sm:block">{candidateName}</span>}
      </header>

      <main className="flex flex-1 flex-col items-center justify-center gap-5 p-4 sm:p-8 lg:flex-row lg:items-start">
        <section
          aria-labelledby="exam-finished-title"
          className="w-full max-w-[560px] rounded-[22px] border border-[#E6E9F2] bg-white p-5 shadow-[0_30px_60px_-40px_rgba(10,22,40,0.3)] sm:rounded-[28px] sm:px-[38px] sm:py-9"
        >
          <span className="font-mono text-[11.5px] font-bold uppercase tracking-[0.14em] text-[#166534]">{t('exam.finished.eyebrow')}</span>
          <h1 id="exam-finished-title" className="mt-2.5 font-display text-[28px] font-extrabold leading-[1.1] tracking-[-0.045em] sm:text-[34px]">
            {t('exam.finished.title')}
          </h1>
          <p className="mb-5 mt-2.5 text-[14.5px] leading-relaxed text-[#4B5468]">{t('exam.finished.help')}</p>

          <ul aria-live="polite">
            {rows.length === 0 && (
              <li className="flex items-center gap-3.5 border-t border-[#EEF1F8] py-3.5 text-[15px] font-semibold">
                <Spinner />
                {t('exam.finished.preparing')}
              </li>
            )}
            {rows.map((row) => {
              const skill = t(SCOPE_SKILL_KEY[row.skill]);
              const scored = row.state === 'scored';
              return (
                <li key={row.skill} className="flex items-center gap-3.5 border-t border-[#EEF1F8] py-3.5 text-[15px] font-semibold first:border-t-0">
                  {scored ? (
                    <i className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-[#E6F4EC] text-[13px] font-extrabold not-italic text-[#166534]" aria-hidden>
                      ✓
                    </i>
                  ) : (
                    <Spinner />
                  )}
                  <span className="min-w-0 flex-1">
                    {scored ? t('exam.finished.scored', { skill }) : t('exam.finished.pending', { skill })}
                    <small className="mt-0.5 block text-[12.5px] font-medium text-[#646D84]">
                      {scored
                        ? row.raw !== undefined
                          ? t('exam.finished.correct', { raw: row.raw, max: row.max })
                          : ''
                        : t('exam.finished.pending_help')}
                    </small>
                  </span>
                  {scored && row.band != null ? <b>{row.band}</b> : !scored ? <span className="text-[#646D84]" aria-hidden>…</span> : null}
                </li>
              );
            })}
          </ul>

          <div className="mt-5 flex flex-col gap-2.5 sm:flex-row">
            <Button type="button" size="lg" className="w-full sm:flex-1" onClick={onSeeResult}>
              {t('exam.finished.see_result')}
            </Button>
            <Button type="button" size="lg" variant="outline" className="w-full sm:flex-1" onClick={onBack}>
              {t('exam.finished.back')}
            </Button>
          </div>
        </section>

        <FeedbackCard attemptId={attemptId} className="w-full max-w-[560px] rounded-[22px] border-[#E6E9F2] p-5 sm:rounded-[28px] sm:p-8 lg:max-w-[460px]" />
      </main>
    </div>
  );
}

function Spinner() {
  return (
    <span
      className="inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full border-[3px] border-[#E6E9F2] border-t-[#4F2BE0] motion-safe:animate-spin"
      aria-hidden
    />
  );
}

export default ExamFinishedScreen;
